import { API_ENDPOINTS } from "@config/api";
import { apiClient } from "@shared/services/api/apiClient";
import type { AuthPort } from "../../domain/ports/auth.port";
import type {
	ChangePasswordRequest,
	ChangePasswordResponse,
	LoginRequest,
	LoginResponse,
	User,
} from "../../domain/types";
import { authMapper } from "../mappers/auth.mapper";

/**
 * Repositorio para la feature de Auth.
 * Gestiona el acceso a datos y comunicación con la API de autenticación.
 */
export class HttpAuthRepository implements AuthPort {
	async login(data: LoginRequest): Promise<LoginResponse> {
		const response = await apiClient.post<LoginResponse>(
			API_ENDPOINTS.auth.login,
			data,
			{
				skipCSRF: true,
			},
		);
		return authMapper.toDomainLogin(response);
	}

	async logout(): Promise<void> {
		try {
			await apiClient.post<null>(API_ENDPOINTS.auth.logout);
		} catch (error: unknown) {
			// Si el logout falla con 401, significa que la sesión ya no es válida en el servidor.
			// Simplemente lo ignoramos para permitir que el frontend limpie su estado local.
			if ((error as { status?: number })?.status !== 401) {
				throw error;
			}
		}
	}

	async me(): Promise<User> {
		const response = await apiClient.get<User>(API_ENDPOINTS.auth.me);
		return authMapper.toDomainUser(response);
	}

	async refresh(): Promise<LoginResponse> {
		const response = await apiClient.post<LoginResponse>(
			API_ENDPOINTS.auth.refresh,
			undefined,
			{
				skipCSRF: true,
			},
		);
		return authMapper.toDomainLogin(response);
	}

	async changePassword(
		data: ChangePasswordRequest,
	): Promise<ChangePasswordResponse> {
		return apiClient.post<ChangePasswordResponse>(
			API_ENDPOINTS.auth.changePassword,
			data,
		);
	}
}

export const authRepository: AuthPort = new HttpAuthRepository();
